import React, { useEffect, useState } from "react";
import { Button, Card, Row, Col } from "react-bootstrap";
import DataTable from "react-data-table-component";
import RangePicker from "./RangePicker";
import {
  ButtonGroup,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  InputGroup,
  InputGroupText,
  Popover,
  PopoverBody,
  PopoverHeader,
  UncontrolledDropdown,
} from "reactstrap";
import { format } from "date-fns";
import ConfirmModal from "../ConfirmModal";

const CustomTable = ({
  data,
  columns,
  onStatusChange,
  buttonLabel = "ADD",
  buttonAction,
  tableTitle,
  showSearch = true,
  searchPlaceholder = "Search here...",
  searchKeys = [],
  showRangePicker = true,
  showAddButton = true,
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [confirmModel, setConfirmModel] = useState({ isOpen: false });
  const togglePopover = () => setPopoverOpen(!popoverOpen);
  const [paginationState, setPaginationState] = useState({
    ...data?.pagination,
  });

  useEffect(() => {
    setPaginationState({ ...(data?.pagination || {}) });
  }, [data]);
  const handleTableChange = (props) => {
    setPaginationState({ ...paginationState, ...props });
    if (onStatusChange) {
      onStatusChange((prev) => ({ ...prev, ...props }));
    }
  };
  const paginationOptions = {
    paginationTotalRows: paginationState?.total || 0,
    paginationPerPage: paginationState?.limit || 10,
    paginationDefaultPage: paginationState?.currentPage || 1,
    sortServer: true,
    onSort: (column, order) =>
      handleTableChange({ sort: order, sortField: column.sortField, page: 1 }),
    onChangePage: (page) => handleTableChange({ page }),
    onChangeRowsPerPage: (limit) => handleTableChange({ limit, page: 1 }),
  };

  const handleActionOnClick = (item, data) => {
    if (!item?.onClick) return;
    if (item?.isConfirm) {
      const toggle = () => setConfirmModel({ isOpen: false });
      setConfirmModel({
        isOpen: true,
        onConfirm: () => {
          item?.onClick(data);
          toggle();
        },
        toggle,
        ...item,
      });
    } else {
      item?.onClick(data);
    }
  };
  const enhancedColumns = columns.map((col) => {
    if (col?.isStatus) {
      col.selector = (data) => {
        return (
          <span
            className={`badge badge-soft-${
              data.isActive == 1 ? "success" : "danger"
            } text-uppercase`}
            onClick={() =>
              col?.onClick &&
              col?.onClick({
                id: data.id,
                isActive: data.isActive == 0 ? "1" : "0",
              })
            }
          >
            {data.isActive == 1 ? "Active" : "Inactive"}
          </span>
        );
      };
    }
    if (col?.dateFormat) {
      col.selector = (row) => {
        return format(new Date(row[col?.dateField]), col.dateFormat);
      };
    }
    if (col?.dropdownItems?.length) {
      col.cell = (data) => {
        return (
          <UncontrolledDropdown className="dropdown d-inline-block">
            <DropdownToggle
              className="btn btn-soft-secondary btn-sm"
              tag="button"
            >
              <i className="ri-more-fill align-middle"></i>
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu-end">
              {col?.dropdownItems.map((item, index) => (
                <DropdownItem
                  key={index}
                  href={item.href}
                  className={item.className}
                  onClick={() => handleActionOnClick(item, data)}
                >
                  <i
                    className={`${item.icon} align-bottom me-2 text-muted`}
                  ></i>
                  {item.label}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        );
      };
    }
    if (col?.actionItems?.length) {
      col.cell = (data, i) => {
        return (
          <ButtonGroup>
            {col?.actionItems.map((item, index) => (
              <button
                type="button"
                key={index}
                className={item.className}
                onClick={() => handleActionOnClick(item, data)}
              >
                <i className={`${item.icon} align-bottom`}></i>
              </button>
            ))}
          </ButtonGroup>
        );
      };
    }
    return {
      ...col,
      name: <span className="font-weight-bold fs-13">{col.name}</span>,
      filter: undefined,
    };
  });

  const handleSearch = () => {
    const searchData = {};
    searchKeys.forEach((key) => {
      searchData[key] = searchText;
    });
    onStatusChange((prev) => ({
      ...prev,
      ...searchData,
    }));
  };
  return (
    <div>
      <Card>
        <Card.Header>
          <Row className="align-items-center justify-content-between">
            <Col md="9">
              {tableTitle && <Card.Title>{tableTitle}</Card.Title>}
              {showAddButton && (
                <Button variant="primary" onClick={buttonAction}>
                  {buttonLabel}
                </Button>
              )}
            </Col>
            {showSearch && (
              <Col md="3" className="d-flex">
                <InputGroup>
                  <Input
                    type={"text"}
                    placeholder={searchPlaceholder}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleSearch();
                      }
                    }}
                  />
                  <InputGroupText
                    style={{ cursor: "pointer" }}
                    onClick={handleSearch}
                  >
                    <i className=" bx bx-search-alt"></i>
                  </InputGroupText>
                </InputGroup>
                {showRangePicker && (
                  <>
                    <Button
                      id="filterPopoverBtn"
                      variant="link"
                      onClick={togglePopover}
                    >
                      <i className="bx bx-filter fs-4"></i>
                    </Button>
                    <Popover
                      placement="bottom-end"
                      className="filter-popover"
                      isOpen={popoverOpen}
                      target="filterPopoverBtn"
                      toggle={togglePopover}
                      offset={[0, 0]}
                    >
                      <PopoverHeader>
                        <div className="d-flex justify-content-between">
                          <p className="h4 m-0">Select Date range</p>
                          <Button
                            variant="link"
                            className="btn-sm"
                            onClick={() => {
                              handleTableChange({ startDate: "", endDate: "" });
                              togglePopover();
                            }}
                          >
                            Reset
                          </Button>
                        </div>
                      </PopoverHeader>
                      <PopoverBody>
                        <RangePicker
                          onStatusChange={onStatusChange}
                          togglePopover={() => togglePopover()}
                        />
                      </PopoverBody>
                    </Popover>
                  </>
                )}
              </Col>
            )}
          </Row>
        </Card.Header>

        <Card.Body>
          <DataTable
            columns={enhancedColumns}
            data={data?.data || []}
            pagination
            paginationServer
            {...paginationOptions}
          />
        </Card.Body>
      </Card>
      <ConfirmModal {...confirmModel} />
    </div>
  );
};

export default CustomTable;
